import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Chip, Collapse, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { generateRecurringDates, parseLocalDate, dateToLocalString } from './recurringDates.ts';

const WEEKDAY_OPTIONS: { day: number; label: string }[] = [
  { day: 0, label: 'א׳' },
  { day: 1, label: 'ב׳' },
  { day: 2, label: 'ג׳' },
  { day: 3, label: 'ד׳' },
  { day: 4, label: 'ה׳' },
];

interface RecurringSeriesPickerProps {
  minDate: Date;
  /** Weekdays that cannot be selected (e.g. Sunday for meeting-room day) */
  disabledWeekdays?: number[];
  shouldDisableDate: (date: Date) => boolean;
  onDatesChange: (dates: string[]) => void;
}

const RecurringSeriesPicker: React.FC<RecurringSeriesPickerProps> = ({
  minDate,
  disabledWeekdays = [],
  shouldDisableDate,
  onDatesChange,
}) => {
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [seriesStart, setSeriesStart] = useState('');
  const [seriesEnd, setSeriesEnd] = useState('');
  const [removedDates, setRemovedDates] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const generated = useMemo(
    () => generateRecurringDates(seriesStart, seriesEnd, weekdays),
    [seriesStart, seriesEnd, weekdays]
  );

  const previewDates = generated.filter((d) => !removedDates.has(d));
  const previewShown = showAll ? previewDates : previewDates.slice(0, 8);
  const hasMore = previewDates.length > 8;

  useEffect(() => {
    onDatesChange(previewDates);
    // previewDates identity changes every render; compare by content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDates.join(',')]);

  const toggleWeekday = (day: number) => {
    if (disabledWeekdays.includes(day)) return;
    setWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
    setRemovedDates(new Set());
  };

  const stringToDate = (dateString: string): Date | null => parseLocalDate(dateString);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          ימי השבוע:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {WEEKDAY_OPTIONS.map(({ day, label }) => {
            const disabled = disabledWeekdays.includes(day);
            const selected = weekdays.includes(day);
            return (
              <Chip
                key={day}
                label={label}
                clickable={!disabled}
                disabled={disabled}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => toggleWeekday(day)}
                sx={{ minWidth: 44, fontWeight: 'bold' }}
              />
            );
          })}
        </Box>
      </Box>

      <Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          מתאריך:
        </Typography>
        <DatePicker
          value={stringToDate(seriesStart)}
          onChange={(newValue) => {
            if (!newValue) {
              setSeriesStart('');
              return;
            }
            const str = dateToLocalString(newValue);
            setSeriesStart(str);
            setRemovedDates(new Set());
            if (seriesEnd && str > seriesEnd) setSeriesEnd(str);
          }}
          minDate={minDate}
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              sx: { direction: 'ltr' },
            },
          }}
        />
      </Box>

      <Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          עד תאריך:
        </Typography>
        <DatePicker
          value={stringToDate(seriesEnd)}
          onChange={(newValue) => {
            if (!newValue) {
              setSeriesEnd('');
              return;
            }
            setSeriesEnd(dateToLocalString(newValue));
            setRemovedDates(new Set());
          }}
          minDate={stringToDate(seriesStart) || minDate}
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              sx: { direction: 'ltr' },
            },
          }}
        />
      </Box>

      {previewDates.length > 0 && (
        <Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {previewDates.length} מועדים נבחרו
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              maxHeight: showAll ? 220 : 'none',
              overflowY: showAll ? 'auto' : 'visible',
            }}
          >
            {previewShown.map((dateStr) => (
              <Chip
                key={dateStr}
                label={dateStr}
                onDelete={() =>
                  setRemovedDates((prev) => new Set(prev).add(dateStr))
                }
                variant="outlined"
                sx={{ direction: 'ltr' }}
              />
            ))}
          </Box>
          {hasMore && (
            <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 1 }}>
              {showAll ? 'הצג פחות' : 'הצג הכל'}
            </Button>
          )}
          <Collapse in={generated.length >= 100}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              מוצגים עד 100 מועדים. קצרו את הטווח במידת הצורך.
            </Typography>
          </Collapse>
        </Box>
      )}

      {weekdays.length > 0 && seriesStart && seriesEnd && previewDates.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          אין מועדים בטווח שנבחר לימים אלה.
        </Typography>
      )}
    </Box>
  );
};

export default RecurringSeriesPicker;
